/**
 * Guards for URLs that come from untrusted data — token creator metadata
 * (`info.socials.*`), user-entered blacklist entries, backend-labelled
 * profiles — before they reach a navigation sink: `window.open`, `<a href>`,
 * `Linking.openURL`, an in-app browser.
 *
 * Only `http:` / `https:` survive. A scheme-less value such as
 * `example.com/path` is upgraded to https because creator-supplied websites
 * are often written that way; any other scheme (`javascript:`, `data:`,
 * `vbscript:`, `file:`, `intent:`, custom app schemes) is dropped instead of
 * being handed to the browser or OS.
 *
 * Only the scheme guarantee is relied upon. Host normalization (lower-casing,
 * IDNA) follows whatever WHATWG URL parser the runtime provides — Node/browsers
 * and React Native's `whatwg-url-without-unicode` differ cosmetically there.
 */
export type ExternalUrlInput = string | string[] | null | undefined;

const SAFE_PROTOCOLS = new Set(['http:', 'https:']);

// `scheme:` at the start counts as an explicit scheme (javascript:, mailto:,
// tel:, intent://...) and is validated as-is. The one ambiguity is a
// scheme-less `host:port/path` — a "scheme" containing a dot that is not
// followed by `//` is treated as a host and gets the https prefix instead.
const SCHEME_PREFIX = /^([a-z][a-z0-9+.-]*):/i;

const hasExplicitScheme = (value: string): boolean => {
  const match = SCHEME_PREFIX.exec(value);
  if (!match) return false;
  const scheme = match[1];
  const rest = value.slice(match[0].length);
  return !(scheme.includes('.') && !rest.startsWith('//'));
};

const firstString = (input: ExternalUrlInput): string | undefined => {
  if (Array.isArray(input)) {
    return input.find((value) => typeof value === 'string' && value.trim().length > 0);
  }
  return typeof input === 'string' ? input : undefined;
};

/**
 * Returns a normalized absolute http(s) URL, or `undefined` when the input is
 * empty, unparsable, or uses any other scheme. The result is safe to pass to
 * `window.open` / `Linking.openURL` / an `href`.
 */
export const toSafeExternalUrl = (input: ExternalUrlInput): string | undefined => {
  const raw = firstString(input)?.trim();
  if (!raw) return undefined;

  const candidate = hasExplicitScheme(raw) ? raw : `https://${raw}`;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return undefined;
  }

  if (!SAFE_PROTOCOLS.has(parsed.protocol) || !parsed.hostname) {
    return undefined;
  }

  // `https://x.com@evil.example/handle` parses with hostname evil.example but
  // every substring-based "is this an X link" check would still pass. No
  // legitimate creator link carries credentials, so drop them outright.
  if (parsed.username || parsed.password) {
    return undefined;
  }

  return parsed.href;
};

const TELEGRAM_PROTOCOLS = new Set(['https:', 'tg:']);

/**
 * Telegram invite links may legitimately use the `tg:` app scheme, which
 * `toSafeExternalUrl` refuses. Same rules otherwise: absolute, no credentials,
 * only `https:` or `tg:`.
 */
export const toSafeTelegramUrl = (input: ExternalUrlInput): string | undefined => {
  const raw = firstString(input)?.trim();
  if (!raw) return undefined;

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return undefined;
  }

  if (!TELEGRAM_PROTOCOLS.has(parsed.protocol) || parsed.username || parsed.password) {
    return undefined;
  }
  if (parsed.protocol === 'https:' && !parsed.hostname) {
    return undefined;
  }

  return parsed.href;
};

const hostnameOf = (input: ExternalUrlInput): string | undefined => {
  const safe = toSafeExternalUrl(input);
  if (!safe) return undefined;
  try {
    return new URL(safe).hostname.toLowerCase().replace(/\.$/, '');
  } catch {
    return undefined;
  }
};

/**
 * Whether a link from untrusted data points at one of `domains` or one of their subdomains, judged
 * on the parsed hostname rather than a substring: `www.x.com` matches `x.com`, while
 * `x.com.evil.example` or `https://evil.example/?x.com` do not. Same parsing as `toSafeExternalUrl`
 * (scheme-less values read as https, credentials and other schemes refused).
 */
export const isUrlOnDomains = (input: ExternalUrlInput, domains: readonly string[]): boolean => {
  const hostname = hostnameOf(input);
  return !!hostname && domains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
};

// Where tweet / post media is served from; kept in step with the web CSP `media-src`.
const POST_MEDIA_DOMAINS = ['twimg.com', 'truthsocial.com', 'universalx.app'] as const;

/**
 * Whether a tweet / post media URL may be handed to a player (`<video>`, hls.js). The URL comes from
 * upstream post data, so only https media on the hosts posts are actually served from is played.
 */
export const isAllowedPostMediaUrl = (url: string | null | undefined): boolean =>
  !!url && /^https:\/\//i.test(url.trim()) && isUrlOnDomains(url, POST_MEDIA_DOMAINS);

const IPV4_HOST = /^\d{1,3}(?:\.\d{1,3}){3}$/;
const LOCAL_HOST_SUFFIXES = ['localhost', 'local', 'internal', 'lan', 'home.arpa'];

/**
 * Whether the browser may fetch a URL from untrusted data itself (e.g. an avatar it reads into a
 * canvas): https only, no credentials, and a public host name — not an IP literal or a local
 * name — so creator-supplied values cannot point the user's browser at their own network.
 */
export const isPublicHttpsUrl = (url: string | null | undefined): boolean => {
  if (!url || !/^https:\/\//i.test(url.trim())) return false;
  const hostname = hostnameOf(url);
  if (!hostname || !hostname.includes('.')) return false;
  if (IPV4_HOST.test(hostname) || hostname.startsWith('[')) return false;
  return !LOCAL_HOST_SUFFIXES.some((suffix) => hostname === suffix || hostname.endsWith(`.${suffix}`));
};
